import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo } from 'react';
import { devWarning, utilStyles, getColorValue, ColorContext, Shimmer } from '@paypalcorp/pp-react-utils';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { textStyles } from '@paypalcorp/pp-react-text';
import { math } from 'polished';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function styles(tokens, colorTokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorLinkAlt = tokens.sysColorLinkAlt,
      sysColorLinkAltHover = tokens.sysColorLinkAltHover,
      sysColorLinkAltActive = tokens.sysColorLinkAltActive,
      sysColorLinkContrast = tokens.sysColorLinkContrast,
      sysColorLinkContrastActive = tokens.sysColorLinkContrastActive,
      sysColorLinkContrastHover = tokens.sysColorLinkContrastHover,
      sysColorLinkMain = tokens.sysColorLinkMain,
      sysColorLinkMainActive = tokens.sysColorLinkMainActive,
      sysColorLinkMainHover = tokens.sysColorLinkMainHover,
      sysSizeIconXs = tokens.sysSizeIconXs,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceXl = tokens.sysSpaceXl,
      sysBorderRadiusXs = tokens.sysBorderRadiusXs;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      shimmerStyles = _utilStyles.shimmerStyles;

  var _textStyles = textStyles(tokens),
      bodyStrong = _textStyles.bodyStrong,
      captionStrong = _textStyles.captionStrong;

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: links_base;\n    text-decoration: none;\n    border-radius: ", ";\n    ", "\n    &:focus {\n      color: ", ";\n      ", ";\n    }\n\n    &:focus,\n    &:hover {\n      text-decoration: underline;\n      cursor: pointer;\n    }\n\n    &:hover {\n      color: ", ";\n      background-color: ", ";\n    }\n\n    &:active {\n      color: ", ";\n      background-color: ", ";\n    }\n  "])), sysBorderRadiusXs, bodyStrong({
    color: getColorValue(colorTokens, sysColorLinkMain, 'contrast')
  }), getColorValue(colorTokens, sysColorLinkMain, 'contrast'), focusStyles.focusOffsetElement, getColorValue(colorTokens, sysColorLinkMainHover, 'contrast'), getColorValue(colorTokens, null, 'hover'), getColorValue(colorTokens, sysColorLinkMainActive, 'contrast'), getColorValue(colorTokens, null, 'active'));
  var baseIcon = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: base_icon;\n    position: relative;\n    padding-inline-start: ", ";\n  "])), math("".concat(sysSizeIconSm, " + ").concat(sysSpaceSm)));
  var baseSmIcon = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: base_sm_icon;\n    padding-inline-start: ", ";\n  "])), math("".concat(sysSizeIconXs, " + ").concat(sysSpaceSm)));
  var secondary = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: secondary;\n    text-decoration: underline;\n    ", "\n\n    &:active {\n      color: ", ";\n      text-decoration: none;\n    }\n\n    &:focus,\n    &:hover {\n      color: ", ";\n      text-decoration: underline;\n    }\n  "])), bodyStrong({
    color: getColorValue(colorTokens, sysColorLinkAlt, 'contrast')
  }), getColorValue(colorTokens, sysColorLinkAltActive, 'contrast'), getColorValue(colorTokens, sysColorLinkAltHover, 'contrast'));
  var shimmer = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: shimmer;\n    ", "\n  "])), shimmerStyles.defaults({
    height: sysSpaceXl
  }));
  var inverse = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: inverse;\n    color: ", ";\n    text-decoration: underline;\n\n    &:visited,\n    &:focus {\n      color: ", ";\n    }\n\n    &:hover {\n      color: ", ";\n    }\n\n    &:active {\n      color: ", ";\n      text-decoration: underline;\n    }\n  "])), getColorValue(colorTokens, sysColorLinkContrast, 'contrast'), getColorValue(colorTokens, sysColorLinkContrast, 'contrast'), getColorValue(colorTokens, sysColorLinkContrastHover, 'contrast'), getColorValue(colorTokens, sysColorLinkContrastActive, 'contrast'));
  var leftIcon = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: left_icon;\n    position: absolute;\n    inset: 0;\n  "])));
  /**
   * Get caption sized styles.
   * @param useSecondaryStyles boolean
   * @param useInverseStyles boolean
   * @returns SerializedStyles
   */

  var getCaptionStyles = function () {
    var useSecondaryStyles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var useInverseStyles = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var color = getColorValue(colorTokens, sysColorLinkMain, 'contrast');
    var variantName = 'caption_primary';

    if (useInverseStyles) {
      variantName = 'caption_inverse';
      color = getColorValue(colorTokens, sysColorLinkContrast, 'contrast');
    }

    if (useSecondaryStyles) {
      variantName = 'caption_secondary';
      color = getColorValue(colorTokens, sysColorLinkAlt, 'contrast');
    }

    return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n      label: ", ";\n      ", ";\n    "])), variantName, captionStrong({
      color: color
    }));
  };

  return {
    base: base,
    baseIcon: baseIcon,
    baseSmIcon: baseSmIcon,
    secondary: secondary,
    shimmer: shimmer,
    inverse: inverse,
    leftIcon: leftIcon,
    getCaptionStyles: getCaptionStyles
  };
}

var _excluded = ["inverse", "secondary", "size", "primaryAlt", "className", "leftIcon", "children", "as", "isLoading"];

var internalHosts = new Set(['', 'paypal.com', 'paypal.cn', 'gopay.cn']);
var NO_REFERRER = /\bnoreferrer\b/;
var NO_OPENER = /\bnoopener\b/;
var Link = forwardRef(function (_ref, ref) {
  var _styles$getCaptionSty;

  var inverse = _ref.inverse,
      secondary = _ref.secondary,
      size = _ref.size,
      primaryAlt = _ref.primaryAlt,
      className = _ref.className,
      leftIcon = _ref.leftIcon,
      children = _ref.children,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'a' : _ref$as,
      isLoading = _ref.isLoading,
      rest = _objectWithoutProperties(_ref, _excluded);

  var colorTokens = React.useContext(ColorContext);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens, colorTokens);
  }, [tokens, colorTokens]);

  if (isLoading) {
    return jsx(Shimmer, _extends({
      css: _styles.shimmer,
      className: className,
      hasStyleDefaults: false,
      isInverse: inverse
    }, rest, {
      "data-ppui": "true"
    }), children);
  }

  var isInternalUrl = function (urlStr) {
    try {
      var url = new URL(urlStr);
      return internalHosts.has(url.hostname);
    } catch (e) {
      devWarning("isInternalUrl('".concat(urlStr, "'}) failed to parse: ").concat(e));
    }

    return false;
  };

  var checkForUnsafe = function (urlStr, rel) {
    if (!(NO_REFERRER.test(rel) && NO_OPENER.test(rel) || isInternalUrl(urlStr))) {
      devWarning("WARNING! '".concat(urlStr, "'\nOpening 3rd party URL in a new window/tab without setting rel to \"noreferrer noopener\" is potentially unsafe."));
    }
  };

  var linkTarget = rest.target,
      _rest$rel = rest.rel,
      rel = _rest$rel === void 0 ? null : _rest$rel,
      _rest$href = rest.href,
      href = _rest$href === void 0 ? '/' : _rest$href;

  if (as === 'a' && (linkTarget === null || linkTarget === void 0 ? void 0 : linkTarget.trim())) {
    var _rel;

    if (!((_rel = rel) === null || _rel === void 0 ? void 0 : _rel.trim())) {
      rel = 'noreferrer noopener';
    } else {
      checkForUnsafe(href, rel);
    }
  }

  var leftIconElement = jsx("span", {
    css: _styles.leftIcon,
    "data-ppui": "true"
  }, leftIcon);
  var isSecondaryStyles = primaryAlt || secondary;
  var linkStyles = [_styles.base, leftIcon && _styles.baseIcon, leftIcon && size === 'sm' && _styles.baseSmIcon, inverse && _styles.inverse, isSecondaryStyles && _styles.secondary, size === 'sm' && ((_styles$getCaptionSty = _styles.getCaptionStyles) === null || _styles$getCaptionSty === void 0 ? void 0 : _styles$getCaptionSty.call(_styles, isSecondaryStyles, inverse))];
  return jsx(as, _extends({
    css: linkStyles,
    className: className,
    ref: ref,
    "data-ppui-info": "links_4.0.0-ql",
    target: linkTarget,
    rel: rel
  }, rest), leftIcon && leftIconElement, children);
});

export { Link, styles as linkStyles };
